import { COMPANY_TIME_ZONE } from '../data/timeZones';

/**
 * Calendar days and times as AuditPlus SHOWS them: the company's US Eastern
 * time for everyone (owner rulings 2026-10-28 — one clock on every screen,
 * times labelled "ET"; before, screens used each device's own zone). Due
 * dates are stored as plain YYYY-MM-DD; completion stamps are UTC ISO
 * timestamps — slicing the latter would put an evening completion on the
 * next day, so convert through the Eastern calendar. Filters and labels
 * must share these helpers or a row can show one date and filter as
 * another.
 */

const EASTERN_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: COMPANY_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' });

/** YYYY-MM-DD of a moment on the company (Eastern) calendar. */
export function companyIsoDay(d: Date): string {
  return EASTERN_DAY.format(d);
}

/**
 * Company-calendar YYYY-MM-DD of an ISO timestamp; '' when missing or
 * unparseable. A plain YYYY-MM-DD is already a calendar day and is returned
 * as-is (parsing it would read UTC midnight and shift it a day).
 */
export function isoToCompanyDay(iso: string | null | undefined): string {
  if (!iso) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : companyIsoDay(d);
}

/** Company-calendar YYYY-MM-DD for N days before `now` (default: today; negative = ahead). */
export function isoDaysAgo(days: number, now: Date = new Date()): string {
  const [y, m, d] = companyIsoDay(now).split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d - days)).toISOString().slice(0, 10);
}

/**
 * How the app SHOWS a calendar day: "Oct 19, 2026" (owner's pick
 * 2026-10-19, so cards, tables and the activity feed match). '' when
 * missing or unparseable. Inputs, filters and stored text keep YYYY-MM-DD.
 */
export function formatDay(iso: string | null | undefined): string {
  const day = isoToCompanyDay(iso);
  if (!day) return '';
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

const EASTERN_TIME = new Intl.DateTimeFormat('en-US', { timeZone: COMPANY_TIME_ZONE, hour: 'numeric', minute: '2-digit' });

/** "2:15 PM ET" — a moment's time of day, always Eastern and labelled. '' when missing or unparseable. */
export function formatTime(iso: string | Date | null | undefined): string {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${EASTERN_TIME.format(d)} ET`;
}

/** "Oct 27, 2026, 2:15 PM ET" — when something happened. '' when missing or unparseable. */
export function formatDateTime(iso: string | Date | null | undefined): string {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${formatDay(d.toISOString())}, ${formatTime(d)}`;
}
